import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  environmentalImpact, 
  calculateWaterUsage, 
  calculateWaterCO2, 
  calculateWasteCO2 
} from '@/lib/data';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Leaf, Droplet, Recycle, Car, Palmtree } from 'lucide-react';

const EnvironmentalImpactCalculator: React.FC<{ className?: string }> = ({ className }) => {
  const [personCount, setPersonCount] = useState<number>(3);
  const [waterEfficiency, setWaterEfficiency] = useState<number>(100);
  const [wasteEfficiency, setWasteEfficiency] = useState<number>(100);
  const [activeTab, setActiveTab] = useState<string>('water');
  
  // Beregninger
  const baseWaterUsage = calculateWaterUsage(personCount);
  const efficientWaterUsage = calculateWaterUsage(personCount, waterEfficiency / 100);
  const waterSaved = baseWaterUsage - efficientWaterUsage;
  
  const baseCO2Water = calculateWaterCO2(baseWaterUsage);
  const efficientCO2Water = calculateWaterCO2(efficientWaterUsage);
  const co2SavedWater = baseCO2Water - efficientCO2Water;
  
  const baseCO2Waste = calculateWasteCO2(personCount);
  const efficientCO2Waste = calculateWasteCO2(personCount, wasteEfficiency / 100);
  const co2SavedWaste = baseCO2Waste - efficientCO2Waste;
  
  const totalCO2Saved = co2SavedWater + co2SavedWaste;
  
  // Konvertering til forståelige enheter
  const carKmEquivalent = Math.round(totalCO2Saved / 0.12); // 120g CO2 per km i en gjennomsnittlig bil
  const treesEquivalent = Math.round(totalCO2Saved / 21); // Et tre absorberer ca. 21 kg CO2 per år
  
  const formatNumber = (num: number) => {
    return num < 1000 ? num.toFixed(0) : (num / 1000).toFixed(1) + 'k';
  };
  
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Leaf className="h-5 w-5 text-nordicBlue" />
          Miljøkalkulator
        </CardTitle>
        <CardDescription>
          Beregn miljøeffekten av å redusere dine kommunale avgifter
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Antall personer i husholdningen</Label>
          <div className="flex items-center gap-4">
            <Input 
              type="number" 
              min={1} 
              max={10}
              value={personCount} 
              onChange={(e) => setPersonCount(Number(e.target.value))}
              className="w-20"
            />
            <Slider 
              min={1} 
              max={10} 
              step={1} 
              value={[personCount]} 
              onValueChange={(value) => setPersonCount(value[0])}
              className="flex-1"
            />
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="water" className="flex items-center gap-1">
              <Droplet className="h-4 w-4" /> Vann
            </TabsTrigger>
            <TabsTrigger value="waste" className="flex items-center gap-1">
              <Recycle className="h-4 w-4" /> Avfall
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        {activeTab === 'water' && (
          <div className="pt-4 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Vannforbruk-reduksjon</Label>
                <Badge variant="outline">{100 - waterEfficiency}%</Badge>
              </div>
              <Slider 
                min={50} 
                max={100}
                step={5}
                value={[waterEfficiency]} 
                onValueChange={(value) => setWaterEfficiency(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                Reduksjon i vannforbruk gjennom vannbesparende utstyr og bevissthet
              </p>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <p className="text-sm font-medium">Med {100 - waterEfficiency}% reduksjon i vannforbruk:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span>Årlig vannbesparelse:</span>
                  <span className="font-medium">{formatNumber(waterSaved)} liter</span>
                </li>
                <li className="flex justify-between">
                  <span>CO₂-reduksjon:</span>
                  <span className="font-medium">{co2SavedWater.toFixed(1)} kg CO₂/år</span>
                </li>
              </ul>
            </div>
          </div>
        )}
        
        {activeTab === 'waste' && (
          <div className="pt-4 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Avfallsreduksjon</Label>
                <Badge variant="outline">{100 - wasteEfficiency}%</Badge>
              </div>
              <Slider 
                min={50} 
                max={100}
                step={5}
                value={[wasteEfficiency]} 
                onValueChange={(value) => setWasteEfficiency(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                Reduksjon i avfallsproduksjon gjennom kildesortering, kompostering og bevisste valg
              </p>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <p className="text-sm font-medium">Med {100 - wasteEfficiency}% reduksjon i avfall:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span>Årlig avfallsreduksjon:</span>
                  <span className="font-medium">
                    {formatNumber((environmentalImpact.averageWastePerPerson * personCount * (1 - wasteEfficiency/100)))} kg
                  </span>
                </li>
                <li className="flex justify-between">
                  <span>CO₂-reduksjon:</span>
                  <span className="font-medium">{co2SavedWaste.toFixed(1)} kg CO₂/år</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex flex-col border-t pt-4">
        <div className="flex justify-between w-full mb-2">
          <span className="text-sm font-medium">Total CO₂-besparelse:</span>
          <span className="text-sm font-bold">{totalCO2Saved.toFixed(1)} kg CO₂/år</span>
        </div>
        
        <div className="grid grid-cols-2 gap-2 w-full">
          <div className="flex items-center gap-2 bg-blue-50 p-2 rounded">
            <Car className="h-4 w-4 text-blue-500" />
            <span className="text-xs">Tilsvarer {formatNumber(carKmEquivalent)} km bilkjøring</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-100 p-2 rounded">
            <Palmtree className="h-4 w-4 text-slate-600" />
            <span className="text-xs">Tilsvarer {treesEquivalent} trær i ett år</span>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default EnvironmentalImpactCalculator; 